import { ReactElement, useEffect, useState } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'

import FormLayoutV2 from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewBusinessClientPrice } from 'features/BusinessClientPrice/components/NewBusinessClientPrice'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canAssignPriceToBusinessClient } from 'features/Users/utils'
import { FormikHelpers } from 'formik'
import { CREATE_BUSINESS_CLIENTS_ROUTE_SERVICES_SCHEDULE_PRICE } from 'features/BusinessClientPrice/mutations'
import { useMutation } from '@apollo/client'
import { INewBusinessClientPriceFormValues } from 'features/BusinessClientPrice/types'
import { format } from 'date-fns'
import { getWeekday, US_DATE_FORMAT } from 'utils/date'
import { sanitizePrice } from 'utils/math'
import Snackbar from 'components/Snackbar'
import { BusinessClientsRouteServicesSchedulePriceDow } from 'gql/generated/graphql'

const messages = defineMessages({
  helmet: {
    id: 'businessClientPrice.new.helmet',
    defaultMessage: 'New Business Client Price | Nexbus Admin'
  }
})

interface ISuccessNotificationProps {
  onClose: () => void
}

const SuccessNotification = ({ onClose }: ISuccessNotificationProps): ReactElement => {
  useEffect(() => {
    setTimeout(() => {
      onClose()
    }, 5 * 1000)
  }, [])

  return (
    <Snackbar.Container>
      <Snackbar.Content>
        <FormattedMessage id="businessClientPrice.new.success.message" defaultMessage="Price saved successfully." />
      </Snackbar.Content>
      <Snackbar.Action onClick={onClose}>
        <FormattedMessage id="businessClientPrice.new.success.action" defaultMessage="Close" />
      </Snackbar.Action>
    </Snackbar.Container>
  )
}

const NewBusinessClientPriceView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const [isShowingSuccessNotification, setIsShowingSuccessNotification] = useState(false)

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])
  const [createBusinessClientsRouteServicesSchedulePrice, { loading, error }] = useMutation(
    CREATE_BUSINESS_CLIENTS_ROUTE_SERVICES_SCHEDULE_PRICE
  )

  if (!canAssignPriceToBusinessClient(login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT} />
  }

  const handleSubmit = async (
    values: INewBusinessClientPriceFormValues,
    { resetForm }: FormikHelpers<INewBusinessClientPriceFormValues>
  ): Promise<void> => {
    try {
      await createBusinessClientsRouteServicesSchedulePrice({
        variables: {
          input: {
            routeServiceIDs: values.routeServices.map(({ id }) => id),
            priceSchedule: {
              startAt: format(values.startDate, US_DATE_FORMAT),
              endAt: values.endDate ? format(values.endDate, US_DATE_FORMAT) : null,
              price: sanitizePrice((values.amount as number) * 100, true),
              dow: values.operationDays.map(
                (day) => getWeekday(day) as unknown as BusinessClientsRouteServicesSchedulePriceDow
              )
            }
          }
        }
      })
      resetForm()
      setIsShowingSuccessNotification(true)
    } catch (error) {
      // Do nothing
    }
  }

  return (
    <FormLayoutV2.Container>
      <NewBusinessClientPrice onSubmit={handleSubmit} loading={loading} error={error} />
      {isShowingSuccessNotification && (
        <SuccessNotification onClose={(): void => setIsShowingSuccessNotification(false)} />
      )}
    </FormLayoutV2.Container>
  )
}

export default withLastUrl(NewBusinessClientPriceView)
